/**
 * Admin dashboard — loads all tenancy data and renders four panels:
 *   1. InviteForm — send email invites to new team members
 *   2. InviteList — pending + accepted invites
 *   3. UserTable  — active users with seat assignment controls
 *   4. ModuleList — read-only module → seat type reference
 */

import { useEffect, useState, useCallback } from 'react'
import { useUser } from '@clerk/clerk-react'
import {
  getUserTenant, listUsers, listSeatTypes, listModules,
  listInvites, assignSeat, removeSeat, revokeInvite, resendInvite, setUserBqeCoreId, setUserEmployment, setUserPosition,
  listModuleGrants, grantModule, revokeModule,
} from '../../services/tenancy.js'
import InviteForm from './InviteForm.jsx'
import InviteList from './InviteList.jsx'
import UserTable  from './UserTable.jsx'
import ModuleList from './ModuleList.jsx'
import PdfTemplateSettings from './PdfTemplateSettings.jsx'
import TimeEntryMigration from './TimeEntryMigration.jsx'
import MilestoneMigration from './MilestoneMigration.jsx'
import ScheduleImageBackfill from './ScheduleImageBackfill.jsx'
import TemplateImageBackfill from './TemplateImageBackfill.jsx'
import BillingInvoiceNumberBackfill from './BillingInvoiceNumberBackfill.jsx'
import BilledFeeSync from './BilledFeeSync.jsx'
import CaMilestoneBackfillPanel from './CaMilestoneBackfillPanel.jsx'
import { confirmDialog, alertDialog } from '../Dialog.jsx'
import TimePolicy from './TimePolicy.jsx'
import ActivityRecordingPanel from './ActivityRecordingPanel.jsx'
import ProgressPolicy from './ProgressPolicy.jsx'
import CrewRosterPanel from './CrewRosterPanel.jsx'
import { useDarkMode } from '../../outline/theme.js'

export default function AdminDashboard({ isModal = false }) {
  const { user }    = useUser()
  const dark        = useDarkMode()
  const [tenant,    setTenant]    = useState(null)
  const [users,     setUsers]     = useState([])
  const [seatTypes, setSeatTypes] = useState([])
  const [modules,   setModules]   = useState([])
  const [invites,   setInvites]   = useState([])
  const [grants,    setGrants]    = useState([])   // module access given to a PERSON, not a seat
  const [positions, setPositions] = useState([])   // Practice Positions (each carries a bill rate)
  const [loading,   setLoading]   = useState(true)
  const [error,     setError]     = useState(null)
  const [tab,       setTab]       = useState('team')  // 'team' | 'templates'

  /* ── Load everything ────────────────────────────────────────────────────── */

  const loadData = useCallback(async (tenantId) => {
    const [usersRes, seatTypesRes, modulesRes, invitesRes, grantsRes] = await Promise.all([
      listUsers(tenantId).catch(() => ({ users: [] })),
      listSeatTypes(tenantId).catch(() => ({ seatTypes: [] })),
      listModules().catch(() => ({ modules: [] })),
      listInvites(tenantId).catch(() => ({ invites: [] })),
      listModuleGrants(tenantId).catch(() => ({ grants: [] })),
    ])
    setUsers(usersRes.users || [])
    setPositions(usersRes.positions || [])
    setSeatTypes(seatTypesRes.seatTypes || [])
    setModules(modulesRes.modules || [])
    setInvites(invitesRes.invites || [])
    setGrants(grantsRes.grants || [])
  }, [])

  useEffect(() => {
    if (!user?.id) return
    async function init() {
      try {
        const t = await getUserTenant(user.id)
        if (!t) { setError('No tenant found for this user.'); setLoading(false); return }
        setTenant(t)
        await loadData(t.id)
      } catch (err) {
        setError(err.message || 'Failed to load admin data.')
      } finally {
        setLoading(false)
      }
    }
    init()
  }, [user?.id, loadData])

  /* ── Actions ────────────────────────────────────────────────────────────── */

  async function handleAssignSeat(clerkUserId, seatTypeId) {
    if (!tenant) return
    try {
      await assignSeat(tenant.id, clerkUserId, seatTypeId)
      const res = await listUsers(tenant.id)
      setUsers(res.users || [])
    } catch (err) {
      alertDialog({ title: 'Could not update seat', message: err.message || 'Unknown error' })
    }
  }

  /**
   * Grant one module to one person, on top of their seat.
   *
   * Bookkeeping gets an explicit confirmation because of what it shows: payroll with individual
   * net pay, owner draws, and the distribution accounts. Every other module is an ordinary grant.
   */
  async function handleGrantModule(clerkUserId, moduleSlug) {
    if (!tenant) return
    const who = users.find(u => u.clerk_user_id === clerkUserId)
    const mod = modules.find(m => m.slug === moduleSlug)
    if (moduleSlug === 'finance-admin') {
      const ok = await confirmDialog({
        title: 'Give Bookkeeping access?',
        message: `${who?.userName || 'This person'} will be able to see every bank transaction, `
          + 'the payroll journal with individual pay, owner draws and the distribution accounts. '
          + 'This is the most sensitive data in the app.',
        confirmLabel: 'Grant access',
      })
      if (!ok) return
    }
    try {
      await grantModule(tenant.id, clerkUserId, moduleSlug, `Granted in Admin by ${user?.primaryEmailAddress?.emailAddress || 'an admin'}.`)
      const res = await listModuleGrants(tenant.id)
      setGrants(res.grants || [])
    } catch (err) {
      alertDialog({ title: `Could not grant ${mod?.name || moduleSlug}`, message: err.message || 'Unknown error' })
    }
  }

  async function handleRevokeModule(clerkUserId, moduleSlug) {
    if (!tenant) return
    const mod = modules.find(m => m.slug === moduleSlug)
    if (!(await confirmDialog({
      title: `Revoke ${mod?.name || moduleSlug}?`,
      message: 'Their seat is not changed — only this individual grant is removed.',
      confirmLabel: 'Revoke',
      danger: true,
    }))) return
    try {
      await revokeModule(tenant.id, clerkUserId, moduleSlug)
      const res = await listModuleGrants(tenant.id)
      setGrants(res.grants || [])
    } catch (err) {
      alertDialog({ title: 'Could not revoke', message: err.message || 'Unknown error' })
    }
  }

  async function handleRemoveSeat(clerkUserId) {
    if (!tenant) return
    if (!(await confirmDialog({ title: 'Remove seat?', message: 'Remove this user\'s seat? They will lose access to the app.', confirmLabel: 'Remove seat', danger: true }))) return
    try {
      await removeSeat(tenant.id, clerkUserId)
      const res = await listUsers(tenant.id)
      setUsers(res.users || [])
    } catch (err) {
      alertDialog({ title: 'Could not remove seat', message: err.message || 'Unknown error' })
    }
  }

  // Save a member's BQE Core resource id (writes the Airtable Member). Returns the promise so the
  // row cell can show its own saving/saved state; no full reload needed.
  function handleSetBqeCoreId(user, value) {
    return setUserBqeCoreId(user.memberId, user.userEmail, value)
  }

  function handleSetEmployment(user, value) {
    return setUserEmployment(tenant.id, user.memberId, value)
  }

  function handleSetPosition(user, positionId) {
    return setUserPosition(user.userEmail, positionId, user.memberId)
  }

  async function handleRevokeInvite(inviteId) {
    if (!tenant) return
    if (!(await confirmDialog({ title: 'Revoke invite?', message: 'Revoke this invite? The link in their email will no longer work.', confirmLabel: 'Revoke invite', danger: true }))) return
    try {
      await revokeInvite(tenant.id, inviteId)
      const res = await listInvites(tenant.id)
      setInvites(res.invites || [])
    } catch (err) {
      alertDialog({ title: 'Could not revoke invite', message: err.message || 'Unknown error' })
    }
  }

  async function handleResendInvite(invite) {
    if (!tenant) return
    if (!(await confirmDialog({ title: 'Resend invite?', message: `Resend the invitation to ${invite.email}? The old link will be invalidated and a fresh email will be sent.`, confirmLabel: 'Resend invite' }))) return
    try {
      await resendInvite(tenant.id, invite)
      const res = await listInvites(tenant.id)
      setInvites(res.invites || [])
    } catch (err) {
      alertDialog({ title: 'Could not resend invite', message: err.message || 'Unknown error' })
    }
  }

  async function refreshAfterInvite() {
    if (!tenant) return
    const res = await listInvites(tenant.id)
    setInvites(res.invites || [])
  }

  /* ── Colors ─────────────────────────────────────────────────────────────── */

  const bg       = dark ? '#0a1820' : '#f4f7f8'
  const surface  = dark ? '#0d1e24' : '#ffffff'
  const border   = dark ? 'rgba(255,255,255,0.08)' : 'rgba(7,39,45,0.10)'
  const textPrim = dark ? 'rgba(207,226,232,0.92)' : '#07272D'
  const textMut  = dark ? 'rgba(91,132,148,0.80)'  : 'rgba(7,39,45,0.50)'

  const panelHeader = {
    padding: '14px 20px', borderBottom: `0.5px solid ${border}`,
    fontFamily: '"Montserrat", sans-serif', fontWeight: 600, fontSize: 10,
    letterSpacing: '0.14em', textTransform: 'uppercase', color: textMut,
  }

  const panel = {
    background: surface, borderRadius: 10,
    border: `0.5px solid ${border}`, marginBottom: 20, overflow: 'hidden',
  }

  /* ── Render ──────────────────────────────────────────────────────────────── */

  const loadingWrap = isModal
    ? { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 48 }
    : { minHeight: '100vh', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }

  if (loading) return (
    <div style={loadingWrap}>
      <span style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 14, color: textMut }}>Loading…</span>
    </div>
  )

  if (error) return (
    <div style={{ ...loadingWrap, padding: 24 }}>
      <span style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 14, color: '#c0392b' }}>{error}</span>
    </div>
  )

  const pendingInvites  = invites.filter(i => i.status === 'pending')
  const acceptedInvites = invites.filter(i => i.status === 'accepted')

  const outerStyle = isModal
    ? { padding: '28px 28px 28px' }
    : { minHeight: '100vh', background: bg, padding: '32px 40px' }

  const tabStyle = (t) => ({
    padding: '7px 16px', fontFamily: '"DM Sans", sans-serif', fontSize: 12, fontWeight: tab === t ? 600 : 400,
    color: tab === t ? textPrim : textMut, background: 'none', border: 'none',
    borderBottom: tab === t ? `2px solid #07272D` : '2px solid transparent',
    cursor: 'pointer', marginBottom: -1,
  })

  return (
    <div style={outerStyle}>
      {/* Header */}
      <div style={{ marginBottom: 20, display: 'flex', alignItems: 'baseline', gap: 12, paddingRight: isModal ? 32 : 0 }}>
        {!isModal && (
          <a href="/" style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: '#1D9E75', textDecoration: 'none' }}>← Projects</a>
        )}
        <div style={{ fontFamily: '"Montserrat", sans-serif', fontWeight: 700, fontSize: 16, letterSpacing: '0.06em', textTransform: 'uppercase', color: textPrim }}>Admin</div>
        {tenant && <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: textMut }}>{tenant.name}</div>}
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', borderBottom: `1px solid ${border}`, marginBottom: 20 }}>
        <button style={tabStyle('team')} onClick={() => setTab('team')}>Team</button>
        <button style={tabStyle('crew')} onClick={() => setTab('crew')}>Crew</button>
        <button style={tabStyle('time')} onClick={() => setTab('time')}>Time</button>
        <button style={tabStyle('progress')} onClick={() => setTab('progress')}>Progress</button>
        <button style={tabStyle('templates')} onClick={() => setTab('templates')}>Templates</button>
        <button style={tabStyle('data')} onClick={() => setTab('data')}>Data</button>
      </div>

      {/* ── Crew tab ─────────────────────────────────────────────────────────────── */}
      {tab === 'crew' && (
        <div style={panel}>
          <div style={panelHeader}>Crew roster</div>
          <CrewRosterPanel />
        </div>
      )}

      {/* ── Time tab ─────────────────────────────────────────────────────────────── */}
      {tab === 'time' && (
        <div style={panel}>
          <div style={panelHeader}>Time-card enforcement</div>
          <TimePolicy dark={dark} />
        </div>
      )}
      {tab === 'time' && (
        <div style={panel}>
          <div style={panelHeader}>Work activity recording</div>
          <ActivityRecordingPanel />
        </div>
      )}

      {/* ── Progress tab ───────────────────────────────────────────────────────── */}
      {tab === 'progress' && (
        <div style={panel}>
          <div style={panelHeader}>Progress Gate enforcement</div>
          <ProgressPolicy dark={dark} />
        </div>
      )}

      {/* ── Data tab ───────────────────────────────────────────────────────────── */}
      {tab === 'data' && (
        <>
          <div style={panel}>
            <div style={panelHeader}>Milestones → Supabase (Stage 1 migration)</div>
            <MilestoneMigration dark={dark} />
          </div>
          <div style={panel}>
            <div style={panelHeader}>Time entries → Supabase (migration)</div>
            <TimeEntryMigration dark={dark} />
          </div>
          <div style={panel}>
            <div style={panelHeader}>Schedule images → Supabase (one-off backfill)</div>
            <ScheduleImageBackfill dark={dark} />
          </div>
          <div style={panel}>
            <div style={panelHeader}>Template images → Supabase (one-off backfill)</div>
            <TemplateImageBackfill dark={dark} />
          </div>
          <div style={panel}>
            <div style={panelHeader}>Billing invoice numbers (one-off backfill)</div>
            <BillingInvoiceNumberBackfill dark={dark} />
          </div>
          <div style={panel}>
            <div style={panelHeader}>Billed Fee ↔ BQE Core sync</div>
            <BilledFeeSync dark={dark} />
          </div>
          <div style={panel}>
            <div style={panelHeader}>RFI/Submittal threads → milestones (one-off backfill)</div>
            <CaMilestoneBackfillPanel dark={dark} />
          </div>
        </>
      )}

      {/* ── Templates tab ──────────────────────────────────────────────────────── */}
      {tab === 'templates' && (
        <>
          {/* WBS Milestone Templates, Room Templates and Control Budget Models moved to the Templates
              tool (Matt, 2026-09-24 — one place for templates; still admin-only there). */}
          <div style={panel}>
            <div style={panelHeader}>Templates moved</div>
            <div style={{ padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <span style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: textMut, lineHeight: 1.5 }}>WBS Milestones, Rooms and Budget Models now live in the Templates tool with every other template.</span>
              <button onClick={() => window.dispatchEvent(new CustomEvent('open-templates-workspace', { detail: { tab: 'milestone' } }))}
                style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 12, fontWeight: 600, color: '#fff', background: '#07272D', border: 'none', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', whiteSpace: 'nowrap' }}>Open Templates</button>
            </div>
          </div>
          <div style={panel}>
            <div style={panelHeader}>PDF Export Settings</div>
            <PdfTemplateSettings dark={dark} />
          </div>
        </>
      )}

      {/* ── Team tab ───────────────────────────────────────────────────────────── */}
      {tab === 'team' && <>

      {/* Invite panel */}
      <div style={panel}>
        <div style={panelHeader}>Invite team member</div>
        {seatTypes.length > 0 && (
          <InviteForm
            tenantId={tenant?.id}
            seatTypes={seatTypes}
            dark={dark}
            onSent={refreshAfterInvite}
          />
        )}
      </div>

      {/* Pending invites */}
      {pendingInvites.length > 0 && (
        <div style={panel}>
          <div style={panelHeader}>Pending invites ({pendingInvites.length})</div>
          <InviteList
            invites={pendingInvites}
            dark={dark}
            onRevoke={handleRevokeInvite}
            onResend={handleResendInvite}
          />
        </div>
      )}

      {/* Active users */}
      <div style={panel}>
        <div style={panelHeader}>Active users ({users.length})</div>
        <UserTable
          users={users}
          seatTypes={seatTypes}
          dark={dark}
          onAssignSeat={handleAssignSeat}
          onRemoveSeat={handleRemoveSeat}
          onSetBqeCoreId={handleSetBqeCoreId}
          onSetEmployment={handleSetEmployment}
          onSetPosition={handleSetPosition}
          positions={positions}
          modules={modules}
          grants={grants}
          onGrant={handleGrantModule}
          onRevoke={handleRevokeModule}
        />
      </div>

      {/* Accepted invites (collapsible history) */}
      {acceptedInvites.length > 0 && (
        <div style={panel}>
          <div style={panelHeader}>Accepted invites</div>
          <InviteList
            invites={acceptedInvites}
            dark={dark}
            onRevoke={() => {}}
          />
        </div>
      )}

      {/* Module access reference */}
      <div style={panel}>
        <div style={panelHeader}>Module access</div>
        <ModuleList modules={modules} seatTypes={seatTypes} dark={dark} />
      </div>

      </>} {/* end team tab */}
    </div>
  )
}
